import { Box, useColorModeValue, useDisclosure } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { DemographicsModal } from "../shared-components/demos-modal";
import { FailedConsolidatedPage } from "../shared-components/failed-consolidated-page";
import { PatientFHIRData } from "../shared-components/fhir-sections";
import { SummaryContent } from "../shared-components/fhir-sections/summaries/content";
import { PatientHeader } from "../shared-components/header";
import { MrModal } from "../shared-components/mr-modal";
import { PatientForm } from "../shared-components/patient-form";
import { PatientMatches } from "../shared-components/patient-matches";
import { DocumentProgressContainer } from "../shared-components/progress-bar";
import { PatientSearch } from "../shared-components/search";
import { PatientTabs } from "../shared-components/tabs";
import { usePatientConsolidatedContext } from "../shared-logic/consolidated-context";
import {
  getValidPatientTab,
  MappedConsolidatedResources,
  PatientConsolidatedStateActionType,
  PatientQueryParamProps,
  PatientTab,
} from "../shared-logic/consolidated-context/reducer";
import { useSharedContext } from "../shared-logic/shared-context";
import { useGetConsolidatedRQ } from "../shared-logic/useGetConsolidatedRQ";
import { useGetEhrDiffRQ } from "../shared-logic/useGetEhrDiffRQ";
import { useGetFacilities } from "../shared-logic/useGetFacilties";
import { useGetFilters } from "../shared-logic/useGetFilters";
import { useGetMedicalRecord } from "../shared-logic/useGetMedicalRecord";
import { useGetPatientInfo } from "../shared-logic/useGetPatientInfo";
import { useGetPatientMatches } from "../shared-logic/useGetPatientMatches";
import { usePatientHieOptOut } from "../shared-logic/usePatientHieOptOut";
import { useQueryDocuments } from "../shared-logic/useQueryDocuments";
import { useSearch } from "../shared-logic/useSearch";
import { useSubmitPatient } from "../shared-logic/useSubmitPatient";

export type SectionCounts = {
  sectionCounts: {
    reports: number;
    documents: number;
  };
};

export function PatientOverview({
  patientId,
  patientQueryParams,
  isImpersonating,
}: {
  patientId: string;
  patientQueryParams?: PatientQueryParamProps | undefined;
  isImpersonating?: boolean;
}) {
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const [isUsingMetriportBundleResources, setIsUsingMetriportBundleResources] =
    useState<boolean>(false);
  const [metriportBundleResources, setMetriportBundleResources] = useState<
    MappedConsolidatedResources | undefined
  >(undefined);
  const [sidePanelContent, setSidePanelContent] = useState<React.ReactNode | undefined>(undefined);

  const cxDidEnableSectionSearch = localStorage.getItem("usingSectionSearch") === "true";
  const [usingSectionSearch, setUsingSectionSearch] = useState<boolean>(cxDidEnableSectionSearch);

  const stickyHeaderBg = useColorModeValue("gray.100", "gray.900");
  const patientFormDisclosure = useDisclosure();
  const patientMRDisclosure = useDisclosure();
  const patientDemosDisclosure = useDisclosure();
  const patientMatchesDisclosure = useDisclosure();

  const [patientTab, setPatientTab] = useState<PatientTab>(
    getValidPatientTab(patientQueryParams?.patientTab)
  );

  const { sharedState } = useSharedContext();
  const isEhr = !!sharedState.ehrDetails;
  const { patientConsolidatedDispatch, patientConsolidatedState } = usePatientConsolidatedContext();
  const { createOrUpdatePatient, isSubmitting } = useSubmitPatient({
    patientId,
    onComplete: () => patientFormDisclosure.onClose(),
  });
  const { patient, isFetchingPatient, setPatient } = useGetPatientInfo({ patientId });
  const { filters } = useGetFilters();
  const { facilities } = useGetFacilities({ isDisabledOnLoad: isEhr });
  const { patientMatches } = useGetPatientMatches({ patientId });
  const { patientHieOptOut, toggleHieOptOut } = usePatientHieOptOut({
    patientId,
    isDisabledOnLoad: isEhr,
  });

  const documentsState = patientConsolidatedState[patientId]?.documents;
  const lastDocQueryState = patientConsolidatedState[patientId]?.lastDocQuery;
  const initialDqRunState = patientConsolidatedState[patientId]?.initialDqRun;
  const consolidatedState = patientConsolidatedState[patientId]?.consolidated;
  const searchResultsState = patientConsolidatedState[patientId]?.searchResults;

  const { existingMrStatus, isMrDownloading, isAtLeastOneMrDownloading, onDownloadMR } =
    useGetMedicalRecord({ patientId });

  const {
    isQueryingDocuments,
    isQueryError,
    dqProgress,
    documentListResult,
    initialDocumentListResult,
    initialDocQueryStatus,
    lastDocQuery,
    searchDocFilters,
    onQueryPatientDocuments,
    onSearchPatientDocuments,
  } = useQueryDocuments({ patientId });

  const {
    consolidatedResources,
    isQueryingConsolidated,
    onQueryPatientConsolidated,
    onInvalidateConsolidated,
    isConsolidatedFailed,
  } = useGetConsolidatedRQ({ patientId });

  const { onSearch, isSearchingResults } = useSearch({ patientId });

  const {
    isFetchingDiff,
    metriportOnlyBundleResources,
    metriportOnlyResourceTypes,
    getMetriportOnlyBundleJob,
  } = useGetEhrDiffRQ({
    ehr: sharedState.ehrDetails?.name,
    ehrPatientId: sharedState.ehrDetails?.ehrPatientId,
    diff: sharedState.ehrDetails?.diff,
  });

  useEffect(() => {
    if (!documentsState && searchDocFilters) {
      onSearchPatientDocuments({ ...searchDocFilters, setFiltersInStorage: false });
    }
  }, [documentsState, searchDocFilters]);

  useEffect(() => {
    async function initDq() {
      if (!initialDqRunState && initialDocumentListResult && initialDocQueryStatus !== undefined) {
        const hasExistingQuery =
          !!initialDocQueryStatus.startedAt || !!initialDocQueryStatus.requestId;
        if (initialDocumentListResult.documents?.length === 0 && !hasExistingQuery) {
          await onQueryPatientDocuments();
        }
        patientConsolidatedDispatch({
          type: PatientConsolidatedStateActionType.setPatientsInitialDqRun,
          patientId,
          initialDqRun: "initialized",
        });
      }
    }
    initDq();
  }, [initialDqRunState, initialDocumentListResult, initialDocQueryStatus]);

  useEffect(() => {
    if (
      consolidatedState === undefined &&
      initialDqRunState &&
      !isQueryingDocuments &&
      !isQueryError
    ) {
      onQueryPatientConsolidated();
    }
  }, [consolidatedState, initialDqRunState, isQueryingDocuments, isQueryError]);

  useEffect(() => {
    if (documentListResult) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsDocuments,
        patientId,
        documents: documentListResult.documents,
      });
    }
  }, [documentListResult]);

  useEffect(() => {
    if (lastDocQuery) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsLastQueryAt,
        patientId,
        lastDocQuery,
      });
    }
  }, [lastDocQuery]);

  useEffect(() => {
    if (consolidatedResources) {
      patientConsolidatedDispatch({
        type: PatientConsolidatedStateActionType.setPatientsConsolidated,
        patientId,
        consolidatedResources,
      });
      getMetriportOnlyBundleJob();
    }
  }, [consolidatedResources]);

  const hasMetriportBundleResources =
    metriportOnlyBundleResources && Object.keys(metriportOnlyBundleResources).length > 0;
  const hasConsolidatedBundleResources =
    consolidatedResources && Object.keys(consolidatedResources).length > 0;
  const canDeriveMetriportResourcesFromBundle =
    hasMetriportBundleResources && hasConsolidatedBundleResources && metriportOnlyResourceTypes;

  useEffect(() => {
    if (!canDeriveMetriportResourcesFromBundle) return;

    const resources = searchResultsState ?? consolidatedResources;

    const metriportOnlyResourceIds = new Set<string>();
    for (const resourceMap of Object.values(metriportOnlyBundleResources)) {
      for (const resourceId of Object.keys(resourceMap)) {
        metriportOnlyResourceIds.add(resourceId);
      }
    }

    const derivedMetriportResources: MappedConsolidatedResources = {};

    Object.entries(resources).forEach(([resourceType, resourceMap]) => {
      // Include resources that are not yet computed by Metriport
      if (!metriportOnlyResourceTypes.includes(resourceType)) {
        derivedMetriportResources[resourceType] = resourceMap;
        return;
      }

      Object.entries(resourceMap).forEach(([resourceId, resource]) => {
        if (metriportOnlyResourceIds.has(resourceId)) {
          let resourceTypeMap = derivedMetriportResources[resourceType];
          if (!resourceTypeMap) {
            resourceTypeMap = derivedMetriportResources[resourceType] = {};
          }
          resourceTypeMap[resourceId] = resource;
        }
      });
    });

    setMetriportBundleResources(derivedMetriportResources);
  }, [
    canDeriveMetriportResourcesFromBundle,
    metriportOnlyBundleResources,
    consolidatedResources,
    metriportOnlyResourceTypes,
    searchResultsState,
  ]);

  useEffect(() => {
    const finalResources = isUsingMetriportBundleResources
      ? metriportBundleResources
      : consolidatedResources;
    if (!finalResources) return;
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.setPatientsConsolidated,
      patientId,
      consolidatedResources: finalResources,
    });
  }, [isUsingMetriportBundleResources, consolidatedResources, metriportBundleResources]);

  function toggleUsingSectionSearch() {
    const newSectionSearchState = !usingSectionSearch;
    setUsingSectionSearch(newSectionSearchState);
    localStorage.setItem("usingSectionSearch", newSectionSearchState.toString());
  }

  function setPatientTabAndParams(patientTab: PatientTab) {
    setPatientTab(patientTab);
    patientQueryParams?.setPatientQueryParams({ patientTab });
  }

  const isLimited = isEhr;
  const isImpersonatingOffset = isImpersonating ? 45 : 0;
  const stickyTopOffset = isEhr ? headerHeight : isImpersonatingOffset;

  function onUserSearch(query: string) {
    patientQueryParams?.clearPatientQueryParams();
    setSidePanelContent(undefined);
    onSearch(query);
  }

  function onUserClearSearch() {
    patientQueryParams?.clearPatientQueryParams();
    setSidePanelContent(undefined);
    patientConsolidatedDispatch({
      type: PatientConsolidatedStateActionType.clearPatientsSearchResults,
      patientId,
    });
  }

  return (
    <>
      {/* V1 Going to add Breadcrumbs */}
      <Box
        p={5}
        maxW={"2560px"}
        minH={"100vh"}
        mx={"auto"}
        bg={useColorModeValue("gray.100", "gray.900")}
      >
        <Box
          bg={useColorModeValue("gray.100", "gray.900")}
          position={isEhr ? "sticky" : "relative"}
          top={0}
          zIndex={5}
        >
          <PatientHeader
            patient={patient}
            isFetchingPatient={isFetchingPatient}
            isQueryingDocuments={isQueryingDocuments || isQueryingConsolidated}
            isAtLeastOneMrDownloading={isAtLeastOneMrDownloading}
            isHieOptedOut={patientHieOptOut}
            usingSectionSearch={usingSectionSearch}
            lastDocQuery={lastDocQueryState}
            actions={{
              onQueryPatientDocuments: async () => {
                onInvalidateConsolidated();
                patientConsolidatedDispatch({
                  type: PatientConsolidatedStateActionType.resetPatientsConsolidated,
                  patientId,
                });
                await onQueryPatientDocuments();
              },
              onGenerateMedicalRecord: patientMRDisclosure.onOpen,
              onEditPatient: patientFormDisclosure.onOpen,
              onDisplayPatientDemos: patientDemosDisclosure.onOpen,
              onViewPatientMatches: patientMatchesDisclosure.onOpen,
              onSetPatientTab: setPatientTabAndParams,
              toggleUsingSectionSearch,
              toggleHieOptOut,
              setUsingMetriportOnlyBundleFromConsolidated: hasMetriportBundleResources
                ? setIsUsingMetriportBundleResources
                : undefined,
            }}
            isFetchingDiff={isFetchingDiff}
            usingMetriportOnlyBundleFromConsolidated={isUsingMetriportBundleResources}
            isLimited={isLimited}
            setHeaderHeight={setHeaderHeight}
            selectedTab={patientTab}
          />
        </Box>
        {isConsolidatedFailed ? (
          <FailedConsolidatedPage />
        ) : (
          <>
            {patientTab === "fhir" && (
              <Box
                id="sticky-header"
                bg={stickyHeaderBg}
                pt={2}
                position={"sticky"}
                top={`${stickyTopOffset}px`}
                zIndex={4}
              >
                <PatientSearch
                  onSearch={onUserSearch}
                  onClear={onUserClearSearch}
                  isLoading={isQueryingDocuments || isQueryingConsolidated}
                />
                <PatientTabs
                  filters={filters}
                  mappedConsolidated={searchResultsState ?? consolidatedState}
                  isIFrame={isEhr}
                  isLimited={isLimited}
                  headerHeight={stickyTopOffset}
                  isLoadingData={
                    isQueryingDocuments ||
                    isQueryingConsolidated ||
                    !(searchResultsState ?? consolidatedState) // to match FHIR sections behavior
                  }
                />
              </Box>
            )}
            {patientTab === "fhir" && (
              <>
                <SummaryContent mappedConsolidated={searchResultsState ?? consolidatedState} />
                <PatientFHIRData
                  filters={filters}
                  mappedConsolidated={searchResultsState ?? consolidatedState}
                  isConsolidatedLoading={
                    isQueryingDocuments || isQueryingConsolidated || isSearchingResults
                  }
                  patientQueryParams={patientQueryParams}
                  usingMetriportOnlyBundleFromConsolidated={isUsingMetriportBundleResources}
                  setSidePanelContent={setSidePanelContent}
                  sidePanelContent={sidePanelContent}
                  isSearchMode={!!searchResultsState}
                  usingSectionSearch={usingSectionSearch}
                />
              </>
            )}
          </>
        )}
      </Box>
      <DocumentProgressContainer dqProgress={dqProgress} />
      <PatientMatches
        isOpen={patientMatchesDisclosure.isOpen || patientTab === "matches"}
        patient={patient}
        patientMatches={patientMatches}
        actions={{
          onClose: () => {
            setPatientTabAndParams("fhir");
            patientMatchesDisclosure.onClose();
          },
        }}
      />
      {patientFormDisclosure.isOpen && (
        <PatientForm
          isOpen={patientFormDisclosure.isOpen}
          selectedPatient={patient}
          facilities={facilities}
          isSubmitting={isSubmitting}
          actions={{
            onCloseForm: patientFormDisclosure.onClose,
            onCreateOrUpdatePatient: async patient => {
              const resp = await createOrUpdatePatient(patient);
              setPatient(resp);
            },
          }}
        />
      )}
      <MrModal
        isOpen={patientMRDisclosure.isOpen}
        isDownloading={isMrDownloading}
        existingMrStatus={existingMrStatus}
        actions={{
          onCloseModal: patientMRDisclosure.onClose,
          onDownloadMR,
        }}
      />
      <DemographicsModal
        isOpen={patientDemosDisclosure.isOpen}
        patient={patient}
        lastDocQuery={lastDocQueryState}
        actions={{
          onCloseModal: patientDemosDisclosure.onClose,
        }}
      />
    </>
  );
}
